'use client'

/**
 * The route-segment error boundary. 63-R081.
 *
 * WHY THIS FILE EXISTS: before it, `grep -rn "ErrorBoundary\|componentDidCatch"` over
 * `frontend/src` returned nothing and `frontend/src/app` held no `error.tsx` and no
 * `global-error.tsx`. Every throw anywhere in the tree therefore unmounted the whole
 * document and the operator saw "Application error: a client-side exception has
 * occurred" with no surviving UI — which is what turned one malformed table cell on
 * Auto Insights into a blank console.
 *
 * WHAT THIS FILE IS NOT: a way to make a defect invisible. A boundary that swallows a
 * shape error and renders a tidy empty region is worse than the crash, because an
 * unexplained empty region on a measures surface reads as a fact about the estate. So
 * this fallback names the route it was covering, states plainly that nothing on the
 * screen behind it can be trusted, and prints the digest a reader can quote. The
 * boundary is the floor under the fixes, not a substitute for them.
 */

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { RegionError } from '@/components/scc/RegionState'

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const pathname = usePathname()

  useEffect(() => {
    // The server strips the message in a production build and leaves the digest, so
    // the digest is what a reader can quote back. Logging it is the only way it
    // reaches anyone who was not watching the console at the moment it threw.
    // eslint-disable-next-line no-console
    console.error('[scc] route boundary caught', pathname, error.digest, error)
  }, [error, pathname])

  return (
    <main className="mx-auto max-w-3xl space-y-4 p-8" role="alert" aria-live="assertive">
      <p className="text-micro uppercase text-brand-muted">
        This screen stopped rendering
      </p>
      <h1 className="font-sans text-2xl font-semibold text-brand-navy">
        {pathname ? `${pathname} could not be rendered.` : 'This screen could not be rendered.'}
      </h1>
      <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
        Nothing that was on this screen before the failure can be relied on. A figure that
        rendered and a figure that did not are indistinguishable from here, so treat the
        whole screen as unread rather than as partially true. The rest of the console is
        unaffected and the record behind this screen is unchanged: this is a rendering
        failure in the console, not a write.
      </p>
      <RegionError
        message={
          error.message ||
          'The failure carried no message, which usually means a production build stripped it. Quote the digest below.'
        }
        httpStatus={null}
        onRetry={reset}
      />
      <p className="font-mono text-xs text-brand-muted">
        {error.digest ? `digest ${error.digest}` : 'no digest was attached to this failure'}
      </p>
    </main>
  )
}
